(() => {
  if (window.rainbowPdpAvailability) return;
  window.rainbowPdpAvailability = true;
  const controllers = new Map();
  const unloaded = new WeakSet();
  const rootSelector = '[data-pdp-section]';
  let suspended = false;

  function mounted(element) {
    if (!element.isConnected) return false;
    const nearestPage = element.closest('product-page');
    if (nearestPage && !nearestPage.hasAttribute('data-pdp-section')) return false;
    for (let node = element; node; node = node.parentElement) {
      if (unloaded.has(node)) return false;
    }
    return true;
  }

  function roots(page) {
    return [...document.querySelectorAll(rootSelector)].filter(root =>
      root.dataset.pdpSection === page.dataset.pdpSection && mounted(root) &&
      (!root.closest('product-page') || root.closest('product-page') === page));
  }

  function elements(page, selector) {
    return [...new Set(roots(page).flatMap(root => [...root.querySelectorAll(selector)]))]
      .filter(element => mounted(element) &&
        (!element.closest('product-page') || element.closest('product-page') === page));
  }

  function nativeFor(page) {
    // Visibility is only a readiness heuristic; it does not prove the SDK has registered a modal.
    return elements(page, '[data-pdp-notify-source]').find(anchor => {
      for (let node = anchor; node; node = node.parentElement) {
        const style = getComputedStyle(node);
        if (node.hidden || style.display === 'none' || style.visibility !== 'visible') return false;
      }
      return true;
    });
  }

  function refresh(page) {
    const controller = controllers.get(page);
    if (!controller || suspended) return;
    const soldOut = page.dataset.pdpUnavailable === 'true';
    const native = soldOut && nativeFor(page);
    if (!soldOut || native) {
      clearTimeout(controller.timer);
      controller.timer = null;
      controller.failed = false;
    } else if (controller.timer === null && !controller.failed) {
      controller.timer = setTimeout(() => {
        controller.timer = null;
        controller.failed = true;
        console.error('Back-in-stock trigger did not appear for section', page.dataset.pdpSection);
        refresh(page);
      }, 10000);
    }
    roots(page).forEach(root => { root.dataset.pdpNotifyReady = String(Boolean(native)); });
    elements(page, '[data-pdp-notify-source]').forEach(anchor => {
      const ready = anchor === native;
      anchor.setAttribute('aria-disabled', String(!ready));
      anchor.tabIndex = ready ? 0 : -1;
    });
    elements(page, '[data-pdp-notify-action]').forEach(button => { button.disabled = !native; });
    elements(page, '[data-pdp-notification-ui]').forEach(ui => {
      const status = ui.querySelector('[data-pdp-notification-status]');
      const retry = ui.querySelector('[data-pdp-notify-retry]');
      const restore = native && (document.activeElement === retry || document.activeElement === status);
      if (status) {
        status.hidden = Boolean(native) || !soldOut;
        const text = controller.failed ? status.dataset.errorText : status.dataset.loadingText;
        if (status.textContent !== text) status.textContent = text;
      }
      if (restore) (ui.querySelector('[data-pdp-notify-action]') || native).focus();
      if (retry) retry.hidden = Boolean(native) || !soldOut || !controller.failed;
    });
  }

  function update(page) {
    const variants = [...page.querySelectorAll('product-variants')]
      .find(element => element.closest('product-page') === page);
    const variant = variants?.currentVariant;
    const soldOut = page.dataset.availability === 'false' ||
      (variants ? variant?.available === false : page.dataset.pdpInitialUnavailable === 'true');
    roots(page).forEach(root => { root.dataset.pdpUnavailable = String(soldOut); });
    const button = elements(page, '[data-js-product-add-to-cart]')[0];
    if (button && variants) button.disabled = variant?.available !== true;
    elements(page, '[js-custom-add-to-cart-sticky], [data-js-sticky-add-to-cart]').forEach(proxy => {
      if (!button) return;
      proxy.disabled = button.disabled || button.classList.contains('disabled');
      proxy.classList.toggle('disabled', proxy.disabled);
      proxy.innerHTML = button.innerHTML;
    });
    elements(page, '[data-js-product-quantity-sticky]').forEach(quantity => {
      if (variants) quantity.style.display = variant?.available ? '' : 'none';
      const input = quantity.querySelector('input');
      if (input && Number.isFinite(variant?.price)) input.dataset.currentPrice = variant.price;
    });
    elements(page, '.mobile-sticky-atc__text').forEach(label => {
      const text = button?.querySelector('[data-js-product-add-to-cart-text]');
      if (text) label.textContent = text.textContent;
    });
    elements(page, '[data-pdp-size]').forEach(option => {
      const disabled = option.dataset.pdpSizeAvailable === 'false' ||
        (option.dataset.pdpSizeCurrent === 'true' && soldOut);
      option.closest('.faux_button')?.classList.toggle('is-unavailable', disabled);
      if (disabled) {
        option.removeAttribute('href');
        option.setAttribute('aria-disabled', 'true');
        option.tabIndex = -1;
      } else {
        option.setAttribute('href', option.dataset.pdpSizeUrl);
        option.removeAttribute('aria-disabled');
        option.removeAttribute('tabindex');
      }
    });
    elements(page, '[data-pdp-single-size]').forEach(size => { size.hidden = !soldOut; });
    refresh(page);
  }

  function mountInlineMobile(page, controller) {
    controller.mobile.forEach((cleanup, sticky) => {
      if (!mounted(sticky)) { cleanup(); controller.mobile.delete(sticky); }
    });
    roots(page).filter(root => root.matches('.mobile-sticky-atc')).forEach(sticky => {
      if (controller.mobile.has(sticky)) return;
      const form = page.querySelector('[data-js-product-add-to-cart]')?.closest('form');
      if (!form) return;
      let intersection;
      function resize() {
        intersection?.disconnect();
        intersection = null;
        sticky.style.display = innerWidth <= 767 ? 'block' : 'none';
        if (innerWidth <= 767 && typeof IntersectionObserver === 'function') {
          // The retained form remains measurable when the purchase button is hidden.
          intersection = new IntersectionObserver(entries => {
            entries.forEach(entry => sticky.classList.toggle('show', !entry.isIntersecting));
          }, { rootMargin: '0px 0px -50px 0px', threshold: 0 });
          intersection.observe(form);
        }
      }
      function click(event) {
        if (!event.target.closest('[data-pdp-purchase]') || page.dataset.pdpUnavailable === 'true') return;
        const target = page.querySelector('.product-size') || form;
        window.scrollTo({ top: target.getBoundingClientRect().top + window.pageYOffset - 100, behavior: 'smooth' });
      }
      window.addEventListener('resize', resize);
      sticky.addEventListener('click', click);
      controller.mobile.set(sticky, () => {
        intersection?.disconnect();
        window.removeEventListener('resize', resize);
        sticky.removeEventListener('click', click);
      });
      resize();
    });
  }

  function dispose(controller) {
    clearTimeout(controller.timer);
    controller.timer = null;
    controller.mobile.forEach(cleanup => cleanup());
    controller.mobile.clear();
  }

  function removeOwnedRoot(root) {
    root.dispatchEvent(new Event('pdp:availability:unmount'));
    root.remove();
  }

  function initialize() {
    if (suspended) return;
    controllers.forEach((controller, page) => {
      if (!mounted(page)) {
        dispose(controller);
        controllers.delete(page);
        roots(page).filter(root => !root.closest('product-page')).forEach(removeOwnedRoot);
      }
    });
    document.querySelectorAll('product-page[data-pdp-section]').forEach(page => {
      if (!mounted(page)) return;
      if (!controllers.has(page)) controllers.set(page, { timer: null, failed: false, mobile: new Map() });
      update(page);
      mountInlineMobile(page, controllers.get(page));
    });
  }

  const observer = new MutationObserver(records => {
    if (records.some(record => record.type === 'childList' &&
      [...record.addedNodes, ...record.removedNodes].some(node => node.nodeType === 1 &&
        (node.matches(`${rootSelector}, [data-pdp-notify-source], [data-pdp-notification-ui], [data-pdp-notify-action]`) ||
          node.querySelector(`${rootSelector}, [data-pdp-notify-source], [data-pdp-notification-ui], [data-pdp-notify-action]`))))) initialize();
    else if (records.some(record => record.type === 'attributes' &&
      (record.target.matches('[data-pdp-notify-source]') ||
        record.target.querySelector('[data-pdp-notify-source]')))) {
      controllers.forEach((_, page) => refresh(page));
    }
  });
  function observe() {
    observer.observe(document.documentElement, {
      subtree: true, childList: true, attributes: true, attributeFilter: ['style', 'class', 'hidden']
    });
  }
  document.addEventListener('VARIANT_CHANGE', event => {
    const page = event.target.closest?.('product-page');
    if (!controllers.has(page) || !mounted(page)) return;
    update(page);
    // Native product-page handlers run after capture; mirror their final button state as well.
    queueMicrotask(() => { if (!suspended && controllers.has(page) && mounted(page)) update(page); });
  }, true);
  document.addEventListener('click', event => {
    const action = event.target.closest?.('[data-pdp-notify-action], [data-pdp-notify-retry], [data-pdp-notify-source]');
    if (!action || !mounted(action)) return;
    const scope = action.closest(rootSelector);
    const page = [...controllers.keys()].find(page =>
      page.dataset.pdpSection === scope?.dataset.pdpSection &&
      (!action.closest('product-page') || action.closest('product-page') === page));
    if (!page) return;
    const native = page.dataset.pdpUnavailable === 'true' && nativeFor(page);
    if (action.matches('[data-pdp-notify-source]')) {
      if (action !== native) { event.preventDefault(); event.stopImmediatePropagation(); }
      return;
    }
    event.preventDefault();
    if (action.matches('[data-pdp-notify-retry]')) {
      const controller = controllers.get(page);
      clearTimeout(controller.timer);
      controller.timer = null;
      controller.failed = false;
      const status = action.closest('[data-pdp-notification-ui]').querySelector('[data-pdp-notification-status]');
      status.hidden = false;
      status.focus();
      refresh(page);
    } else if (native) native.click();
  }, true);
  document.addEventListener('shopify:section:unload', event => {
    const pages = [...controllers.keys()].filter(page => event.target === page || event.target.contains(page));
    pages.forEach(page => {
      roots(page).filter(root => !event.target.contains(root)).forEach(removeOwnedRoot);
      dispose(controllers.get(page));
      controllers.delete(page);
    });
    unloaded.add(event.target);
    initialize();
  });
  document.addEventListener('shopify:section:load', event => { unloaded.delete(event.target); initialize(); });
  window.addEventListener('pagehide', () => {
    suspended = true;
    observer.disconnect();
    controllers.forEach(dispose);
  });
  window.addEventListener('pageshow', () => { suspended = false; observe(); initialize(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
  observe();
})();
